import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type SourceRoiResult } from '@whinerp/client';
import { formatMoney, todayIn } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/** Lead source ROI (G-208). Cost and return only where spend is recorded; otherwise said. */
export function SourceRoiPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = can('analytics.view');
  const [by, setBy] = useState<'source' | 'campaign'>('campaign');
  const to = todayIn(format);
  const from = new Date(Date.parse(to) - 365 * 86_400_000).toISOString().slice(0, 10);
  const report = useQuery<SourceRoiResult, ApiError>({ queryKey: ['source-roi', by, from, to], queryFn: () => api.sourceRoi({ query: { from, to, by } }), enabled, placeholderData: (previous) => previous });
  if (!enabled || !report.data) return null;
  const money = (amount: string | null) => (amount === null ? t('roi.noCost') : formatMoney(amount, format, { currency: report.data.currency }));
  return (
    <Panel data-region="source-roi">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <SectionLabel>{t('roi.title')}</SectionLabel>
          <select className="ms-auto h-input rounded-control border border-control bg-surface px-2 text-body text-ink" data-roi-by aria-label={t('roi.by')}
            value={by} onChange={(event) => setBy(event.target.value as 'source')}>
            <option value="campaign">{t('roi.byCampaign')}</option>
            <option value="source">{t('roi.bySource')}</option>
          </select>
        </div>
        <div data-scroll-container className="overflow-x-auto">
          <table className="w-full text-dense" data-roi-table>
            <thead>
              <tr className="text-meta text-ink-meta">
                <th className="p-1 text-start font-normal">{t(by === 'campaign' ? 'roi.campaign' : 'roi.source')}</th>
                <th className="p-1 text-end font-normal">{t('roi.leads')}</th>
                <th className="p-1 text-end font-normal">{t('roi.won')}</th>
                <th className="p-1 text-end font-normal">{t('roi.revenue')}</th>
                <th className="p-1 text-end font-normal">{t('roi.cost')}</th>
                <th className="p-1 text-end font-normal">{t('roi.return')}</th>
              </tr>
            </thead>
            <tbody>
              {report.data.rows.map((row) => (
                <tr key={row.key} data-roi-row={row.key}>
                  <td className="p-1 text-ink">{row.key}</td>
                  <td className="p-1 text-end">{row.leads}</td>
                  <td className="p-1 text-end">{row.wonDeals}</td>
                  <td className="p-1 text-end">{money(row.revenue)}</td>
                  <td className="p-1 text-end text-ink-600">{money(row.cost)}</td>
                  <td className="p-1 text-end" data-roi-return>{row.roi === null ? '-' : `${String(row.roi)}×`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}
